import { useState, useEffect, useRef } from 'react';
import { listOrgCollection, readOrgFile, writeOrgFile, deleteOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { scanReferencesToEntity } from '../../utils/intelligence';
import { callWorker } from '../../utils/workerClient';
import AISuggestButton from '../shared/AISuggestButton';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import EntitySelector from '../shared/EntitySelector';
import { BG_SUBTLE, BG_CONTENT, BG_HOVER_LT, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_BAD, STATUS_WARN, STATUS_INFO, STATUS_NEUTRAL, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_LIST_ITEM, FONT_STAT_VALUE } from '../../theme';
import ConfirmDialog from '../shared/ConfirmDialog';
import { dateStatus } from '../../utils/dateStatus';
import { OBJECTIVE_STATUSES } from '../../utils/enums';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:                 'Objetivos de seguridad',
    newObj:                '+ Nuevo objetivo',
    save:                  'Guardar',
    saving:                'Guardando…',
    saveOk:                '✓ Guardado',
    saveError:             'Error al guardar',
    unsavedLabel:          'Cambios sin guardar',
    unsavedConfirm:        'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:    'Salir sin guardar',
    saveAndLeave:          'Guardar y salir',
    loading:               'Cargando…',
    emptyTitle:            'No hay objetivos de seguridad.',
    emptyHint:             'Crea el primer objetivo con el botón "+ Nuevo objetivo".',
    cntTotal:              'Total',
    cntOnTrack:            'En curso',
    cntAtRisk:             'En riesgo',
    cntAchieved:           'Logrado',
    cntNotAchieved:        'No logrado',
    colRef:                'Referencia',
    colArea:               'Área',
    colObjective:          'Objetivo',
    viewBtn:               'Ver',
    colTimescale:          'Fecha límite',
    colOwner:              'Responsable',
    colStatus:             'Estado del objetivo',
    colActions:            'Acciones',
    editBtn:               'Editar',
    formTitleNew:          'Nuevo objetivo',
    formTitleEdit:         'Editar objetivo',
    formTitleView:         'Ver objetivo',
    backToList:            '← Volver a la lista',
    sectionA:              'Detalles del objetivo',
    sectionB:              'Plan para conseguir el objetivo',
    fRef:                  'Referencia',
    fArea:                 'Área',
    fObjective:            'Objetivo',
    fMeasurement:          'Método de medición',
    ttMeasurement:         'Cómo se medirá el cumplimiento de este objetivo. Ejemplos: porcentaje de completitud, número de elementos, fecha de entrega.',
    fTarget:               'Meta',
    fDeadline:             'Fecha límite',
    fApprovedBy:           'Aprobado por',
    fApprovalDate:         'Fecha de aprobación',
    fOwner:                'Responsable del objetivo',
    fStatus:               'Estado del objetivo',
    fProgressUpdates:      'Actualizaciones de progreso',
    progressPlaceholder:   'Descripción de la actualización…',
    addBtn:                'Añadir',
    fPlanToAchieve:        'Plan para conseguir el objetivo',
    fResources:            'Recursos necesarios',
    fEvaluation:           'Método de evaluación',
    fPlanStatus:           'Estado del plan',
    fResponsiblePersons:   'Persona responsable',
    formSave:              'Guardar objetivo',
    formCancel:            'Cancelar',
    validationError:       'Por favor completa los campos obligatorios marcados con *.',
    statusNotStarted:      'No iniciado',
    statusOnTrack:         'En curso',
    statusAtRisk:          'En riesgo',
    statusAchieved:        'Logrado',
    statusNotAchieved:     'No logrado',
    planNotStarted:        'No iniciado',
    planInProgress:        'En progreso',
    planCompleted:         'Completado',
    aiError:               'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded:     'Servidores ocupados, inténtalo de nuevo en un momento.',
    aiConsentRequired:     'Activa el procesamiento con IA en Configuración para usar esta función.',
    deleteBtn:             'Eliminar',
    deleteFormBtn:         'Eliminar objetivo',
    deleteConfirm:         '¿Eliminar el objetivo "{ref}"? Esta acción no se puede deshacer.',
    deleteError:           'Error al eliminar. Inténtalo de nuevo.',
    deleteSelected:        'Eliminar seleccionados',
    deleteSelectedConfirm: '¿Eliminar {n} objetivo(s) seleccionado(s)? Esta acción no se puede deshacer.',
    deleteSelectedError:   'No se pudo eliminar {n} objetivo(s). Los demás se eliminaron correctamente.',
    nSelected:             '{n} seleccionado(s)',
    selectAll:             'Seleccionar todo',
    filterObjective:       'Objetivo',
    filterObjectivePh:     'Buscar…',
    filterOwner:           'Responsable',
    filterStatus:          'Estado',
    filterAll:             'Todos',
    filterNSelected:       '{n} seleccionados',
    filterNoSelection:     'Todos',
    sectionRelations:      'Relaciones',
    fLinkedMetrics:        'Métricas vinculadas',
    fLinkedRisks:          'Riesgos vinculados',
    metPlaceholder:        'Buscar métrica…',
    riskPlaceholder:       'Buscar riesgo…',
    required:              '*',
    requiredLegend:        '* Campos obligatorios',
    badgeOverdue:          'Vencido',
    badgeSoon:             'Vence pronto',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    title:                 'Security objectives',
    newObj:                '+ New objective',
    save:                  'Save',
    saving:                'Saving…',
    saveOk:                '✓ Saved',
    saveError:             'Save error',
    unsavedLabel:          'Unsaved changes',
    unsavedConfirm:        'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:    'Leave without saving',
    saveAndLeave:          'Save and leave',
    loading:               'Loading…',
    emptyTitle:            'No security objectives yet.',
    emptyHint:             'Create the first objective using the "+ New objective" button.',
    cntTotal:              'Total',
    cntOnTrack:            'On Track',
    cntAtRisk:             'At Risk',
    cntAchieved:           'Achieved',
    cntNotAchieved:        'Not Achieved',
    colRef:                'Reference',
    colArea:               'Area',
    colObjective:          'Objective',
    viewBtn:               'View',
    colTimescale:          'Timescale',
    colOwner:              'Owner',
    colStatus:             'Objective status',
    colActions:            'Actions',
    editBtn:               'Edit',
    formTitleNew:          'New objective',
    formTitleEdit:         'Edit objective',
    formTitleView:         'View objective',
    backToList:            '← Back to list',
    sectionA:              'Objective details',
    sectionB:              'Plan to achieve objective',
    fRef:                  'Ref',
    fArea:                 'Area',
    fObjective:            'Objective',
    fMeasurement:          'Measurement method',
    ttMeasurement:         'How achievement of this objective will be measured. Examples: completion percentage, number of items, delivery date.',
    fTarget:               'Target',
    fDeadline:             'Deadline',
    fApprovedBy:           'Approved by',
    fApprovalDate:         'Approval date',
    fOwner:                'Objective owner',
    fStatus:               'Objective status',
    fProgressUpdates:      'Progress updates',
    progressPlaceholder:   'Update description…',
    addBtn:                'Add',
    fPlanToAchieve:        'Plan to achieve objective',
    fResources:            'Resources required',
    fEvaluation:           'Evaluation method',
    fPlanStatus:           'Plan status',
    fResponsiblePersons:   'Person responsible',
    formSave:              'Save objective',
    formCancel:            'Cancel',
    validationError:       'Please complete the required fields marked with *.',
    statusNotStarted:      'Not Started',
    statusOnTrack:         'On Track',
    statusAtRisk:          'At Risk',
    statusAchieved:        'Achieved',
    statusNotAchieved:     'Not Achieved',
    planNotStarted:        'Not Started',
    planInProgress:        'In Progress',
    planCompleted:         'Completed',
    aiError:               'Could not get suggestion. Please try again.',
    aiErrorOverloaded:     'Servers are busy, please try again in a moment.',
    aiConsentRequired:     'Enable AI processing in Settings to use this feature.',
    deleteBtn:             'Delete',
    deleteFormBtn:         'Delete objective',
    deleteConfirm:         'Delete objective "{ref}"? This cannot be undone.',
    deleteError:           'Delete failed. Please try again.',
    deleteSelected:        'Delete selected',
    deleteSelectedConfirm: 'Delete {n} selected objective(s)? This cannot be undone.',
    deleteSelectedError:   '{n} objective(s) could not be deleted. The rest were deleted successfully.',
    nSelected:             '{n} selected',
    selectAll:             'Select all',
    filterObjective:       'Objective',
    filterObjectivePh:     'Search…',
    filterOwner:           'Owner',
    filterStatus:          'Status',
    filterAll:             'All',
    filterNSelected:       '{n} selected',
    filterNoSelection:     'All',
    sectionRelations:      'Relationships',
    fLinkedMetrics:        'Linked metrics',
    fLinkedRisks:          'Linked risks',
    metPlaceholder:        'Search metric…',
    riskPlaceholder:       'Search risk…',
    required:              '*',
    requiredLegend:        '* Required fields',
    badgeOverdue:          'Overdue',
    badgeSoon:             'Due soon',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── STATUS COLORS ─────────────────────────────────────────────────────────────
const OBJ_STATUS_COLORS = {
  'Not Started':  { bg: BG_MUTED, text: TEXT_MUTED },
  'On Track':     { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  'At Risk':      { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Achieved':     { bg: '#14532d', text: SURFACE_WHITE },
  'Not Achieved': { bg: ERROR_BG, text: ERROR_TEXT },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
async function genRef(orgId, objectivePrefix) {
  const year = new Date().getFullYear();
  const prefix = `${objectivePrefix}-${year}-`;
  try {
    const items = await listOrgCollection(orgId, 'objectives');
    let max = 0;
    items.forEach(o => {
      if (o.ref && o.ref.startsWith(prefix)) {
        const n = parseInt(o.ref.slice(prefix.length), 10);
        if (!isNaN(n) && n > max) max = n;
      }
    });
    return `${prefix}${String(max + 1).padStart(3, '0')}`;
  } catch {
    const ts = Date.now().toString().slice(-4);
    return `${prefix}${ts}`;
  }
}


function blankForm(ref) {
  return {
    ref,
    area:                '',
    objective:           '',
    measurementMethod:   '',
    target:              '',
    deadline:            '',
    approvedBy:          null,
    approvalDate:        null,
    owner:               '',
    status:              'Not Started',
    progressUpdates:     [],
    planToAchieve:       '',
    resourcesRequired:   '',
    evaluationMethod:    '',
    planStatus:          'Not Started',
    responsiblePersons:  [],
    linkedMetricIds:     [],
    linkedRiskIds:       [],
    norms:               [],
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const inputSt = (err) => ({
  ...FONT_LABEL, fontSize: '0.85rem',
  width: '100%', boxSizing: 'border-box',
  padding: '0.45rem 0.65rem',
  border: `1px solid ${err ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6,
  background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none',
});

const labelSt = {
  ...FONT_SMALL, fontSize: '0.7rem',
  color: TEXT_MUTED, letterSpacing: '0.03em',
  textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
};

const thSt = {
  ...FONT_TINY, fontSize: '0.67rem',
  color: TEXT_MUTED, letterSpacing: '0.04em', textTransform: 'uppercase',
  padding: '0.55rem 0.7rem', textAlign: 'left',
  borderBottom: `1px solid ${BORDER_DEFAULT}`, background: BG_SUBTLE,
  whiteSpace: 'nowrap',
};

const tdSt = {
  ...FONT_LIST_ITEM, fontSize: '0.84rem',
  color: TEXT_DEFAULT, padding: '0.65rem 0.7rem',
  borderBottom: `1px solid ${BG_SHELL}`, verticalAlign: 'middle',
};

// ── TOOLTIP ───────────────────────────────────────────────────────────────────
function Tooltip({ text }) {
  const [vis, setVis] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      onMouseEnter={() => setVis(true)}
      onMouseLeave={() => setVis(false)}
    >
      <span style={{
        ...FONT_TINY, fontSize: '0.6rem', fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 14, height: 14, borderRadius: '50%',
        border: `1px solid ${TEXT_DISABLED}`, color: TEXT_DISABLED,
        cursor: 'default', userSelect: 'none', lineHeight: 1,
      }}>i</span>
      {vis && (
        <span style={{
          ...FONT_SMALL, fontSize: '0.75rem',
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)',
          background: '#1a1a1a', color: SURFACE_WHITE,
          maxWidth: 280, width: 'max-content', borderRadius: 4,
          padding: '6px 10px', zIndex: 1000,
          lineHeight: 1.45,
          whiteSpace: 'normal', pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}>
          {text}
        </span>
      )}
    </span>
  );
}

// ── SECTION HEADING ───────────────────────────────────────────────────────────
const sectionHeading = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  margin: '0 0 1rem',
  paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};

function Divider() {
  return <div style={{ borderTop: `1px solid ${BORDER_DEFAULT}`, margin: '1.5rem 0' }} />;
}

// ── STATUS BADGES ─────────────────────────────────────────────────────────────
function ObjStatusBadge({ status, t }) {
  const labels = {
    'Not Started':  t.statusNotStarted,
    'On Track':     t.statusOnTrack,
    'At Risk':      t.statusAtRisk,
    'Achieved':     t.statusAchieved,
    'Not Achieved': t.statusNotAchieved,
  };
  const colors = OBJ_STATUS_COLORS[status] || OBJ_STATUS_COLORS['Not Started'];
  return (
    <span style={{
      ...FONT_SMALL, fontSize: '0.7rem', fontWeight: 600,
      background: colors.bg, color: colors.text,
      borderRadius: 5, padding: '0.2rem 0.55rem',
      whiteSpace: 'nowrap',
    }}>
      {labels[status] || status}
    </span>
  );
}

// ── MULTI-SELECT FILTER DROPDOWN ──────────────────────────────────────────────
function MultiSelectFilter({ options, values, onChange, allLabel, nSelectedTpl, getLabel }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter(x => x !== v));
    else                    onChange([...values, v]);
  };

  const summary = values.length === 0
    ? allLabel
    : nSelectedTpl.replace('{n}', values.length);

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...FONT_LABEL,
          background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6,
          padding: '0.3rem 0.5rem',
          color: TEXT_DEFAULT,
          cursor: 'pointer', textAlign: 'left', minWidth: 160,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span style={{ color: TEXT_DISABLED, fontSize: '0.7rem' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 2, zIndex: 200,
          background: SURFACE_WHITE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: 220, maxHeight: 260, overflowY: 'auto', padding: '0.3rem 0',
        }}>
          {options.length === 0 && (
            <div style={{ ...FONT_SMALL, fontSize: '0.75rem', padding: '0.4rem 0.65rem', color: TEXT_DISABLED }}>—</div>
          )}
          {options.map(opt => {
            const checked = values.includes(opt);
            return (
              <label
                key={opt}
                style={{ ...FONT_SMALL, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.65rem', color: TEXT_DEFAULT, cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = BG_CONTENT; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(opt)} />
                <span>{getLabel ? getLabel(opt) : opt}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
export default function ObjectivesView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [objectives, setObjectives]     = useState(null);   // null = loading
  const [people, setPeople]             = useState([]);
  const [metricOptions, setMetricOptions] = useState([]);
  const [riskOptions, setRiskOptions] = useState([]);
  const [formSaveState, setFormSaveState] = useState('idle');
  const [formOpen, setFormOpen]         = useState(false);
  const [formMode, setFormMode]         = useState('edit'); // 'create' | 'edit' | 'view'
  const [editingId, setEditingId]       = useState(null);   // null = new
  const [form, setForm]                 = useState(null);
  const [formErrors, setFormErrors]     = useState({});
  const [newProgress, setNewProgress]   = useState({ date: '', note: '' });
  const [aiSuggestingMeasurement, setAiSuggestingMeasurement] = useState(false);
  const [aiErrorMeasurement, setAiErrorMeasurement]           = useState(null);
  const [aiSuggestingPlan, setAiSuggestingPlan]               = useState(false);
  const [aiErrorPlan, setAiErrorPlan]                         = useState(null);
  const [selectedIds, setSelectedIds]         = useState(new Set());
  const [deleteError, setDeleteError]         = useState(null);
  const [confirmState, setConfirmState]       = useState(null);
  const [scanningDelete, setScanningDelete]   = useState(false);
  const [dirty, setDirty]                     = useState(false);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next form save reports back via onSaveResult instead of running
  // the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef                     = useRef(false);
  const lastProcessedAppTickRef               = useRef(0);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filterText,   setFilterText]   = useState('');
  const [filterOwners, setFilterOwners] = useState([]); // multi-select
  const [filterStatus, setFilterStatus] = useState('');

  useScrollToTopOnFormOpen(editingId !== null);

  // ── Load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    listOrgCollection(org.id, 'objectives')
      .then(items => {
        const sorted = [...items].sort((a, b) =>
          (a.ref || '').localeCompare(b.ref || '')
        );
        setObjectives(sorted);
      })
      .catch(() => setObjectives([]));
  }, [org.id]);

  useEffect(() => {
    readOrgFile(org.id, 'people.json')
      .then(data => setPeople(Array.isArray(data.people) ? data.people : []))
      .catch(() => setPeople([]));
  }, [org.id]);

  useEffect(() => {
    listOrgCollection(org.id, 'metrics')
      .then(items => setMetricOptions(items.map(m => ({ id: m.id, label: `${m.ref} — ${m.name}` }))))
      .catch(() => setMetricOptions([]));
  }, [org.id]);

  useEffect(() => {
    listOrgCollection(org.id, 'risks')
      .then(items => setRiskOptions(items.map(r => ({
        id: r.id,
        label: (r.ref ? r.ref + ' — ' : '') + (r.riskSummary || r.id),
      }))))
      .catch(() => setRiskOptions([]));
  }, [org.id]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const setF = (field, val) => {
    if (formMode !== 'view') setDirty(true);
    setForm(prev => prev ? { ...prev, [field]: val } : prev);
  };

  const clearErr = (field) => {
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleAISuggestMeasurement = async () => {
    setAiSuggestingMeasurement(true);
    setAiErrorMeasurement(null);
    const prompt = `You are an ISO 27001:2022 information security management expert.\nGiven the following security objective, suggest a measurement method for it.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nObjective: ${form.objective}\nArea: ${form.area}\nTarget: ${form.target}\nReturn exactly this JSON:\n{\n"measurementMethod": "specific measurable method in 1-2 sentences"\n}`;
    const result = await callWorker({ prompt, maxTokens: 250 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiErrorMeasurement(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggestingMeasurement(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiErrorMeasurement(t.aiError);
      setAiSuggestingMeasurement(false);
      return;
    }
    if (parsed.measurementMethod && !form.measurementMethod) setF('measurementMethod', parsed.measurementMethod);
    setAiSuggestingMeasurement(false);
  };

  const handleAISuggestPlan = async () => {
    setAiSuggestingPlan(true);
    setAiErrorPlan(null);
    const basePrompt = `You are an ISO 27001:2022 information security management expert.\nGiven the following security objective, suggest a plan to achieve it and a method to evaluate its achievement.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nObjective: ${form.objective}\nArea: ${form.area}\nTarget: ${form.target}`;
    const contextLine = form.measurementMethod.trim()
      ? `\nMeasurement method: ${form.measurementMethod}`
      : '';
    const schemaLine = `\nReturn exactly this JSON:\n{\n"planToAchieve": "3-5 concrete actions to achieve the objective, one per line",\n"evaluationMethod": "how to evaluate achievement in 1-2 sentences"\n}`;
    const prompt = `${basePrompt}${contextLine}${schemaLine}`;
    const result = await callWorker({ prompt, maxTokens: 500 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiErrorPlan(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggestingPlan(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiErrorPlan(t.aiError);
      setAiSuggestingPlan(false);
      return;
    }
    if (parsed.planToAchieve && !form.planToAchieve) setF('planToAchieve', parsed.planToAchieve);
    if (parsed.evaluationMethod && !form.evaluationMethod) setF('evaluationMethod', parsed.evaluationMethod);
    setAiSuggestingPlan(false);
  };

  // ── App-level guard: bridge App tick into local tick (which the existing
  //    saveRequestTick watcher below picks up to call handleFormSave). When
  //    no form is open, report success directly so App can navigate.
  //    See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (formOpen) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Save request watcher (triggered by "Save and leave") ──────────────────
  useEffect(() => {
    if (saveRequestTick > 0) { handleFormSave(); setSaveRequestTick(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Form open/close ───────────────────────────────────────────────────────
  const objToForm = (obj) => ({
    ref:                 obj.ref || '',
    area:                obj.area || '',
    objective:           obj.objective || '',
    measurementMethod:   obj.measurementMethod || '',
    target:              obj.target || '',
    deadline:            obj.deadline || obj.timescale || '',
    approvedBy:          obj.approvedBy || null,
    approvalDate:        obj.approvalDate || null,
    owner:               obj.owner || '',
    status:              OBJECTIVE_STATUSES.includes(obj.status) ? obj.status : 'Not Started',
    progressUpdates:     Array.isArray(obj.progressUpdates) ? obj.progressUpdates : [],
    planToAchieve:       typeof obj.planToAchieve === 'string'
                           ? obj.planToAchieve
                           : Array.isArray(obj.planToAchieve)
                             ? obj.planToAchieve.join('\n')
                             : '',
    resourcesRequired:   obj.resourcesRequired || '',
    evaluationMethod:    obj.evaluationMethod || '',
    planStatus:          obj.planStatus || 'Not Started',
    responsiblePersons:  Array.isArray(obj.responsiblePersons) ? obj.responsiblePersons : [],
    linkedMetricIds:     Array.isArray(obj.linkedMetricIds) ? obj.linkedMetricIds : [],
    linkedRiskIds:       Array.isArray(obj.linkedRiskIds) ? obj.linkedRiskIds : [],
    norms:               Array.isArray(obj.norms) ? obj.norms : [],
  });

  const openNew = async () => {
    const objectivePrefix = (org.refPrefixes && org.refPrefixes.objective) || 'obj';
    const ref = await genRef(org.id, objectivePrefix);
    setForm(blankForm(ref));
    setFormMode('create');
    setEditingId(null);
    setFormErrors({});
    setNewProgress({ date: '', note: '' });
    setFormOpen(true);
    setDirty(false);
  };

  const openEdit = (obj) => {
    setForm(objToForm(obj));
    setFormMode('edit');
    setEditingId(obj.id);
    setFormErrors({});
    setNewProgress({ date: '', note: '' });
    setFormOpen(true);
    setDirty(false);
  };

  const openView = (obj) => {
    setForm(objToForm(obj));
    setFormMode('view');
    setEditingId(obj.id);
    setFormErrors({});
    setNewProgress({ date: '', note: '' });
    setFormOpen(true);
    setDirty(false);
  };

  const closeForm = () => {
    setDirty(false);
    setFormOpen(false);
    setForm(null);
    setFormErrors({});
  };

  const handleFormCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── Form save → write to disk immediately ────────────────────────────────
  const handleFormSave = async () => {
    const errs = {};
    if (!form.ref?.trim())               errs.ref = true;
    if (!form.area?.trim())              errs.area = true;
    if (!form.objective?.trim())         errs.objective = true;
    if (!form.measurementMethod?.trim()) errs.measurementMethod = true;
    if (!form.target?.trim())            errs.target = true;
    if (!form.deadline?.trim())          errs.deadline = true;
    if (!form.owner?.trim())             errs.owner = true;

    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }

    const now = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';
    setFormSaveState('saving');

    try {
      let fileId;
      let obj;
      let prevLinkedMetricIds = [];

      if (editingId === null) {
        fileId = form.ref.trim().toLowerCase();
        obj = {
          id:                  fileId,
          orgId:               org.id,
          ref:                 form.ref.trim(),
          area:                form.area.trim(),
          objective:           form.objective.trim(),
          measurementMethod:   form.measurementMethod.trim(),
          target:              form.target.trim(),
          deadline:            form.deadline,
          approvedBy:          form.approvedBy,
          approvalDate:        form.approvalDate,
          owner:               form.owner.trim(),
          status:              form.status,
          progressUpdates:     form.progressUpdates,
          planToAchieve:       form.planToAchieve.split('\n').map(s => s.trim()).filter(Boolean),
          evaluationMethod:    form.evaluationMethod,
          resourcesRequired:   form.resourcesRequired.trim() || null,
          responsiblePersons:  Array.isArray(form.responsiblePersons) ? form.responsiblePersons : [],
          linkedMetricIds:     form.linkedMetricIds,
          linkedRiskIds:       form.linkedRiskIds,
          norms:               form.norms,
          createdAt:           now,
          updatedAt:           now,
        };
        obj.changeLog = [];
      } else {
        fileId = editingId;
        const existing = (objectives || []).find(o => o.id === editingId) || {};
        prevLinkedMetricIds = existing.linkedMetricIds || [];
        obj = {
          ...existing,
          id:                  fileId,
          orgId:               org.id,
          ref:                 form.ref.trim(),
          area:                form.area.trim(),
          objective:           form.objective.trim(),
          measurementMethod:   form.measurementMethod.trim(),
          target:              form.target.trim(),
          deadline:            form.deadline,
          approvedBy:          form.approvedBy,
          approvalDate:        form.approvalDate,
          owner:               form.owner.trim(),
          status:              form.status,
          progressUpdates:     form.progressUpdates,
          planToAchieve:       form.planToAchieve.split('\n').map(s => s.trim()).filter(Boolean),
          evaluationMethod:    form.evaluationMethod,
          resourcesRequired:   form.resourcesRequired.trim() || null,
          responsiblePersons:  Array.isArray(form.responsiblePersons) ? form.responsiblePersons : [],
          linkedMetricIds:     form.linkedMetricIds,
          linkedRiskIds:       form.linkedRiskIds,
          norms:               form.norms,
          updatedAt:           now,
        };
        delete obj.baseline;
        delete obj.currentValue;
        obj.changeLog = appendChangeLog(existing, obj, changedBy);
      }

      await writeOrgFile(org.id, `objectives/${fileId}.json`, obj);

      // Bidirectional sync — update metrics' objectiveId
      const addedMetricIds = form.linkedMetricIds.filter(id => !prevLinkedMetricIds.includes(id));
      const removedMetricIds = prevLinkedMetricIds.filter(id => !form.linkedMetricIds.includes(id));
      for (const metId of addedMetricIds) {
        try {
          const metric = await readOrgFile(org.id, `metrics/${metId}.json`);
          if (metric.objectiveId !== obj.id) {
            await writeOrgFile(org.id, `metrics/${metId}.json`, { ...metric, objectiveId: obj.id, updatedAt: now });
          }
        } catch {
          // non-fatal
        }
      }
      for (const metId of removedMetricIds) {
        try {
          const metric = await readOrgFile(org.id, `metrics/${metId}.json`);
          if (metric.objectiveId === obj.id) {
            await writeOrgFile(org.id, `metrics/${metId}.json`, { ...metric, objectiveId: null, updatedAt: now });
          }
        } catch {
          // non-fatal
        }
      }

      const items = await listOrgCollection(org.id, 'objectives');
      setObjectives([...items].sort((a, b) => (a.ref || '').localeCompare(b.ref || '')));

      if (editingId === null) {
        setEditingId(fileId);
        setFormMode('edit');
      }
      setDirty(false);
      setFormErrors({});
      setFormSaveState('ok');
      setTimeout(() => {
        setFormSaveState('idle');
      }, 2000);
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(true);
      }
    } catch {
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(false);
      }
      setFormSaveState('error');
      setTimeout(() => setFormSaveState('idle'), 3000);
    }
  };

  // ── Delete handlers ───────────────────────────────────────────────────────
  const toggleAll = (checked) => {
    if (checked) {
      const visible = (objectives || []).filter(o => {
        if (filterText) {
          const q = filterText.trim().toLowerCase();
          if (q && !(o.objective || '').toLowerCase().includes(q)) return false;
        }
        if (filterOwners.length > 0 && !filterOwners.includes(o.owner)) return false;
        if (filterStatus && o.status !== filterStatus) return false;
        return true;
      });
      setSelectedIds(new Set(visible.map(o => o.id)));
    } else setSelectedIds(new Set());
  };

  const toggleOne = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  // ── §11 rule 42 helpers ─────────────────────────────────────────────────
  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const handleDeleteOne = async (obj) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'objective', obj.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm.replace('{ref}', obj.ref || obj.id),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `objectives/${obj.id}.json`);
            setObjectives(prev => (prev || []).filter(o => o.id !== obj.id));
            setSelectedIds(prev => { const next = new Set(prev); next.delete(obj.id); return next; });
          } catch {
            setDeleteError(t.deleteError);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  const handleDeleteFromForm = async () => {
    if (editingId === null) return;
    const obj = (objectives || []).find(o => o.id === editingId) || { id: editingId, ref: form?.ref };
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'objective', obj.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      setConfirmState({
        message: t.deleteConfirm.replace('{ref}', obj.ref || obj.id),
        confirmLabel: t.deleteFormBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `objectives/${obj.id}.json`);
            setObjectives(prev => (prev || []).filter(o => o.id !== obj.id));
            setSelectedIds(prev => { const next = new Set(prev); next.delete(obj.id); return next; });
            closeForm();
          } catch {
            setDeleteError(t.deleteError);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.size) return;
    const items = (objectives || []).filter(o => selectedIds.has(o.id));
    if (!items.length) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(o => scanReferencesToEntity(org.id, 'objective', o.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((o, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(o);
      else blocked.push({ record: o, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohort) => {
      let failCount = 0;
      const deletedIds = new Set();
      await Promise.all(
        cohort.map(async (o) => {
          try {
            await deleteOrgFile(org.id, `objectives/${o.id}.json`);
            deletedIds.add(o.id);
          } catch {
            failCount++;
          }
        })
      );
      setObjectives(prev => (prev || []).filter(o => !deletedIds.has(o.id)));
      setSelectedIds(prev => { const next = new Set(prev); deletedIds.forEach(id => next.delete(id)); return next; });
      if (failCount > 0) {
        setDeleteError(t.deleteSelectedError.replace('{n}', String(failCount)));
      }
    };
    if (blocked.length === 0) {
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{n}', String(items.length)),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(items);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable);
      },
    });
  };

  // ── Add progress update ───────────────────────────────────────────────────
  const handleAddProgress = () => {
    if (!newProgress.date || !newProgress.note.trim()) return;
    setF('progressUpdates', [
      ...(form.progressUpdates || []),
      { date: newProgress.date, note: newProgress.note.trim() },
    ]);
    setNewProgress({ date: '', note: '' });
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (objectives === null) {
    return (
      <div style={{ ...FONT_LABEL, fontSize: '0.85rem', padding: '2rem', color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  // ── Computed summary ──────────────────────────────────────────────────────
  const total        = objectives.length;
  const onTrack      = objectives.filter(o => o.status === 'On Track').length;
  const atRisk       = objectives.filter(o => o.status === 'At Risk').length;
  const achieved     = objectives.filter(o => o.status === 'Achieved').length;
  const notAchieved  = objectives.filter(o => o.status === 'Not Achieved').length;

  // ── Filter options & filtered list ───────────────────────────────────────
  const ownerOptions = Array.from(
    new Set(objectives.map(o => o.owner).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const STATUS_OPTIONS = OBJECTIVE_STATUSES;
  const STATUS_LABELS = {
    'Not Started':  t.statusNotStarted,
    'On Track':     t.statusOnTrack,
    'At Risk':      t.statusAtRisk,
    'Achieved':     t.statusAchieved,
    'Not Achieved': t.statusNotAchieved,
  };

  const filteredObjectives = objectives.filter(o => {
    if (filterText) {
      const q = filterText.trim().toLowerCase();
      if (q && !(o.objective || '').toLowerCase().includes(q)) return false;
    }
    if (filterOwners.length > 0 && !filterOwners.includes(o.owner)) return false;
    if (filterStatus && o.status !== filterStatus) return false;
    return true;
  });

  const hasErrors   = Object.keys(formErrors).length > 0;
  const someChecked = selectedIds.size > 0;
  const allChecked  = filteredObjectives.length > 0 && filteredObjectives.every(o => selectedIds.has(o.id));

  // ── Form view ─────────────────────────────────────────────────────────────
  if (formOpen && form) {
    const objStatuses = OBJECTIVE_STATUSES.map(value => ({ value, label: STATUS_LABELS[value] }));
    const planStatuses = [
      { value: 'Not Started', label: t.planNotStarted },
      { value: 'In Progress', label: t.planInProgress },
      { value: 'Completed',   label: t.planCompleted },
    ];

    const cardSt = {
      background: BG_SURFACE, border: `1px solid ${STATUS_NEUTRAL.bg}`,
      borderRadius: 10, padding: '1.5rem 1.75rem',
    };

    const isView = formMode === 'view';
    return (
      <div>
        {/* Form header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '0.75rem', marginBottom: '2rem',
        }}>
          <div>
            <button
              onClick={handleFormCancel}
              style={{
                ...FONT_LABEL,
                background: 'none', border: 'none', cursor: 'pointer',
                color: BRAND_PRIMARY, padding: 0,
                display: 'block', marginBottom: '0.5rem',
              }}
            >{t.backToList}</button>
            <h2 style={{
              ...FONT_PAGE_TITLE, fontSize: '1.4rem',
              color: TEXT_HEADING, margin: 0,
            }}>
              {isView
                ? `${t.formTitleView}: ${form.ref}`
                : (editingId === null ? t.formTitleNew : `${t.formTitleEdit}: ${form.ref}`)}
            </h2>
          </div>
        </div>

        {!isView && (
          <p style={{ ...FONT_SMALL, fontSize: '0.7rem', color: ERROR_TEXT, margin: '0 0 2rem', letterSpacing: '0.04em' }}>{t.requiredLegend}</p>
        )}

        {formSaveState === 'error' && (
          <div style={{
            ...FONT_LABEL,
            background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
            borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '1.25rem',
            color: ERROR_TEXT,
          }}>
            {t.saveError}
          </div>
        )}

        {hasErrors && (
          <div style={{
            ...FONT_LABEL, fontSize: '0.82rem',
            background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
            borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1.25rem',
            color: ERROR_TEXT,
          }}>
            {t.validationError}
          </div>
        )}

        <fieldset disabled={isView || formSaveState === 'saving'} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* ── Section A — Objective details ── */}
          <div style={cardSt}>
            <div style={sectionHeading}>{t.sectionA}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelSt}>{t.fRef} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <input
                  type="text" value={form.ref}
                  onChange={e => setF('ref', e.target.value)}
                  style={inputSt(formErrors.ref)}
                />
              </div>
              <div>
                <label style={labelSt}>{t.fArea} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <input
                  type="text" value={form.area}
                  onChange={e => setF('area', e.target.value)}
                  style={inputSt(formErrors.area)}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelSt}>{t.fObjective} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
              <textarea
                rows={3} value={form.objective}
                onChange={e => setF('objective', e.target.value)}
                style={{ ...inputSt(formErrors.objective), resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem', marginBottom: '0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{ ...FONT_SMALL, fontSize: '0.7rem', color: formErrors.measurementMethod ? ERROR_TEXT : TEXT_MUTED, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                    {t.fMeasurement} <span style={{ color: ERROR_TEXT }}>{t.required}</span><span className="ai-spark">✦</span>
                  </span>
                  <Tooltip text={t.ttMeasurement} />
                </div>
                <AISuggestButton onClick={handleAISuggestMeasurement} loading={aiSuggestingMeasurement} disabled={!form.objective.trim() || !org.aiProcessingConsent} lang={lang} />
                {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
              </div>
              {aiErrorMeasurement && (
                <div style={{
                  ...FONT_SMALL, fontSize: '0.75rem',
                  color: ERROR_TEXT, marginBottom: '0.3rem',
                }}>{aiErrorMeasurement}</div>
              )}
              <textarea
                rows={2} value={form.measurementMethod}
                onChange={e => setF('measurementMethod', e.target.value)}
                style={{ ...inputSt(formErrors.measurementMethod), resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelSt}>{t.fTarget} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
              <textarea
                rows={2} value={form.target}
                onChange={e => setF('target', e.target.value)}
                style={{ ...inputSt(formErrors.target), resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelSt}>{t.fDeadline} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <input
                  type="date" value={form.deadline}
                  onChange={e => setF('deadline', e.target.value)}
                  style={inputSt(formErrors.deadline)}
                />
              </div>
              <div>
                <label style={labelSt}>{t.fOwner} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <PeopleSelector orgId={org.id} lang={lang} value={form.owner || ''} onChange={val => { setF('owner', val); clearErr('owner'); }} disabled={formSaveState === 'saving'} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelSt}>{t.fApprovedBy}</label>
                <PeopleSelector orgId={org.id} lang={lang} value={form.approvedBy || ''} onChange={val => setF('approvedBy', val || null)} disabled={formSaveState === 'saving'} />
              </div>
              <div>
                <label style={labelSt}>{t.fApprovalDate}</label>
                <input
                  type="date"
                  value={form.approvalDate || ''}
                  onChange={e => setF('approvalDate', e.target.value || null)}
                  style={inputSt(false)}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelSt}>{t.fStatus} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
              <select
                value={form.status}
                onChange={e => setF('status', e.target.value)}
                style={inputSt(false)}
              >
                {objStatuses.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Progress updates */}
            <div>
              <label style={{ ...labelSt, marginBottom: '0.5rem' }}>{t.fProgressUpdates}</label>

              {form.progressUpdates.length > 0 && (
                <div style={{ marginBottom: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  {form.progressUpdates.map((entry, i) => (
                    <div key={i} style={{
                      ...FONT_LABEL, fontSize: '0.8rem',
                      color: TEXT_DEFAULT, lineHeight: 1.4,
                    }}>
                      <span style={{ color: TEXT_DISABLED, marginRight: '0.5rem' }}>{entry.date}</span>
                      {entry.note}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <input
                  type="date"
                  value={newProgress.date}
                  onChange={e => setNewProgress(p => ({ ...p, date: e.target.value }))}
                  style={{ ...inputSt(false), width: 'auto', minWidth: 140 }}
                />
                <input
                  type="text"
                  value={newProgress.note}
                  onChange={e => setNewProgress(p => ({ ...p, note: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddProgress(); } }}
                  placeholder={t.progressPlaceholder}
                  style={{ ...inputSt(false), flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleAddProgress}
                  style={{
                    ...BTN_PRIMARY, ...FONT_LABEL,
                    border: 'none',
                    borderRadius: 6, padding: '0.45rem 0.85rem', cursor: 'pointer',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >{t.addBtn}</button>
              </div>
            </div>
          </div>

          {/* ── Section Relations — Relationships ── */}
          <div style={cardSt}>
            <div style={sectionHeading}>{t.sectionRelations}</div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelSt}>{t.fLinkedMetrics}</label>
              <EntitySelector
                items={metricOptions}
                selectedIds={form.linkedMetricIds}
                onAdd={(id) => setF('linkedMetricIds', [...form.linkedMetricIds, id])}
                onRemove={(id) => setF('linkedMetricIds', form.linkedMetricIds.filter(x => x !== id))}
                placeholder={t.metPlaceholder}
                disabled={isView || formSaveState === 'saving'}
              />
            </div>
            <div>
              <label style={labelSt}>{t.fLinkedRisks}</label>
              <EntitySelector
                items={riskOptions}
                selectedIds={form.linkedRiskIds}
                onAdd={(id) => setF('linkedRiskIds', [...form.linkedRiskIds, id])}
                onRemove={(id) => setF('linkedRiskIds', form.linkedRiskIds.filter(x => x !== id))}
                placeholder={t.riskPlaceholder}
                disabled={isView || formSaveState === 'saving'}
                chipBg={STATUS_INFO.bg}
                chipColor="#1d4ed8"
              />
            </div>
          </div>

          {/* ── Section B — Plan ── */}
          <div style={cardSt}>
            <div style={sectionHeading}>{t.sectionB}</div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem', marginBottom: '0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{ ...FONT_SMALL, fontSize: '0.7rem', color: TEXT_MUTED, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                    {t.fPlanToAchieve}<span className="ai-spark">✦</span>
                  </span>
                </div>
                <AISuggestButton onClick={handleAISuggestPlan} loading={aiSuggestingPlan} disabled={!form.objective.trim() || !org.aiProcessingConsent} lang={lang} />
                {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
              </div>
              {aiErrorPlan && (
                <div style={{
                  ...FONT_SMALL, fontSize: '0.75rem',
                  color: ERROR_TEXT, marginBottom: '0.3rem',
                }}>{aiErrorPlan}</div>
              )}
              <textarea
                rows={4} value={form.planToAchieve}
                onChange={e => setF('planToAchieve', e.target.value)}
                style={{ ...inputSt(false), resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelSt}>{t.fResources}</label>
              <textarea
                rows={2} value={form.resourcesRequired}
                onChange={e => setF('resourcesRequired', e.target.value)}
                style={{ ...inputSt(false), resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelSt}>{t.fEvaluation}<span className="ai-spark">✦</span></label>
              <textarea
                rows={2} value={form.evaluationMethod}
                onChange={e => setF('evaluationMethod', e.target.value)}
                style={{ ...inputSt(false), resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={labelSt}>{t.fResponsiblePersons}</label>
                <PeopleSelector
                  orgId={org.id}
                  lang={lang}
                  value={Array.isArray(form.responsiblePersons) ? form.responsiblePersons : []}
                  onChange={val => setF('responsiblePersons', Array.isArray(val) ? val : [])}
                  multiple
                  disabled={formSaveState === 'saving'}
                />
              </div>
              <div>
                <label style={labelSt}>{t.fPlanStatus} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
                <select
                  value={form.planStatus}
                  onChange={e => setF('planStatus', e.target.value)}
                  style={{ ...inputSt(false), maxWidth: 240 }}
                >
                  {planStatuses.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

        </div>
        </fieldset>
        <Divider />

        {/* Sticky action bar */}
        {!isView && (
          <div style={{
            position: 'sticky',
            bottom: 0,
            marginLeft: '-1.5rem',
            marginRight: '-1.5rem',
            marginBottom: '-2.5rem',
            marginTop: '1rem',
            padding: '0.85rem 1.5rem',
            background: BG_SURFACE,
            borderTop: `1px solid ${BORDER_DEFAULT}`,
            zIndex: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div>
                {editingId !== null && (
                  <button
                    onClick={handleDeleteFromForm}
                    disabled={formSaveState === 'saving'}
                    style={{
                      ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, fontSize: '0.75rem',
                      borderRadius: 8,
                      padding: '0.5rem 1rem',
                      cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                    }}
                  >{t.deleteFormBtn}</button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {dirty && (
                  <span style={{
                    ...FONT_LABEL,
                    color: UNSAVED_AMBER,
                  }}>{t.unsavedLabel}</span>
                )}
                <button
                  onClick={handleFormCancel}
                  disabled={formSaveState === 'saving'}
                  style={{
                    ...BTN_SECONDARY, ...FONT_BUTTON, fontWeight: 400, fontSize: '0.88rem',
                    borderRadius: 8,
                    padding: '0.6rem 1.25rem', cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                  }}
                >{t.formCancel}</button>
                <button
                  onClick={handleFormSave}
                  disabled={formSaveState === 'saving'}
                  style={{
                    ...BTN_PRIMARY,
                    ...FONT_BUTTON, fontWeight: 700, fontSize: '0.88rem',
                    background: formSaveState === 'saving' ? TEXT_MUTED : formSaveState === 'ok' ? BRAND_PRIMARY_DARK : BTN_PRIMARY.background,
                    border: 'none',
                    borderRadius: 8, padding: '0.6rem 1.6rem',
                    cursor: formSaveState === 'saving' ? 'not-allowed' : 'pointer',
                  }}
                >{formSaveState === 'saving' ? t.saving : formSaveState === 'ok' ? t.saveOk : t.formSave}</button>
              </div>
            </div>
          </div>
        )}
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          content={confirmState?.content}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          confirmColor={confirmState?.confirmColor}
          secondaryAction={confirmState?.secondaryAction}
        />
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.75rem',
      }}>
        <div>
          <h2 style={{
            ...FONT_PAGE_TITLE, fontSize: '1.4rem',
            color: TEXT_HEADING, margin: 0,
          }}>{t.title}</h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={openNew}
            style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, fontSize: '0.85rem',
              border: 'none',
              borderRadius: 8, padding: '0.45rem 1rem', cursor: 'pointer',
            }}
          >{t.newObj}</button>
        </div>
      </div>

      {/* Delete error */}
      {deleteError && (
        <div style={{
          ...FONT_LABEL, fontSize: '0.82rem',
          background: ERROR_BG, border: `1px solid ${ERROR_BORDER}`,
          borderRadius: 8, padding: '0.65rem 1rem', marginBottom: '1rem',
          color: ERROR_TEXT,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          {deleteError}
          <button
            onClick={() => setDeleteError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: ERROR_TEXT, fontSize: '1rem', padding: '0 0 0 0.75rem', lineHeight: 1 }}
          >×</button>
        </div>
      )}

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.75rem' }}>
        {[
          { label: t.cntTotal,       value: total,       color: TEXT_DEFAULT },
          { label: t.cntOnTrack,     value: onTrack,     color: BRAND_PRIMARY_DARK },
          { label: t.cntAtRisk,      value: atRisk,      color: STATUS_WARN.text },
          { label: t.cntAchieved,    value: achieved,    color: '#14532d' },
          { label: t.cntNotAchieved, value: notAchieved, color: FIELD_ERROR_BORDER }, // TODO: P6b — verify color role
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
            borderRadius: 10, padding: '0.9rem 1.25rem',
            flex: 1, minWidth: 88,
          }}>
            <div style={{
              ...FONT_SMALL,
              color: TEXT_DEFAULT, letterSpacing: '0.04em',
              textTransform: 'uppercase', marginBottom: '0.35rem',
            }}>{label}</div>
            <div style={{
              ...FONT_STAT_VALUE, color,
            }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      {objectives.length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, fontSize: '0.7rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterObjective}</span>
            <input type="text" value={filterText} onChange={e => setFilterText(e.target.value)} placeholder={t.filterObjectivePh}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT, minWidth: 200 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, fontSize: '0.7rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterOwner}</span>
            <MultiSelectFilter
              options={ownerOptions}
              values={filterOwners}
              onChange={setFilterOwners}
              allLabel={t.filterNoSelection}
              nSelectedTpl={t.filterNSelected}
              getLabel={(v) => resolvePerson(v, people)}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, fontSize: '0.7rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              {STATUS_OPTIONS.map(s => (<option key={s} value={s}>{STATUS_LABELS[s]}</option>))}
            </select>
          </div>
        </div>
      )}

      {/* Empty state */}
      {objectives.length === 0 && (
        <div style={{
          background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 10, padding: '3rem', textAlign: 'center',
        }}>
          <p style={{
            ...FONT_BODY, fontSize: '1rem',
            color: TEXT_MUTED, marginBottom: '0.5rem', marginTop: 0,
          }}>{t.emptyTitle}</p>
          <p style={{
            ...FONT_LABEL, fontSize: '0.8rem',
            color: TEXT_DISABLED, marginBottom: '1.5rem', marginTop: 0,
          }}>{t.emptyHint}</p>
          <button
            onClick={openNew}
            style={{
              ...BTN_PRIMARY, ...FONT_BUTTON, fontSize: '0.9rem',
              border: 'none',
              borderRadius: 8, padding: '0.65rem 1.5rem', cursor: 'pointer',
            }}
          >{t.newObj}</button>
        </div>
      )}

      {/* Bulk action bar */}
      {someChecked && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.5rem 0.75rem', marginBottom: '0.6rem',
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
        }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
            {t.nSelected.replace('{n}', String(selectedIds.size))}
          </span>
          <button
            onClick={handleDeleteSelected}
            style={{
              ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_TINY, fontSize: '0.68rem',
              borderRadius: 5,
              padding: '0.2rem 0.5rem', cursor: 'pointer',
            }}
          >{t.deleteSelected}</button>
        </div>
      )}

      {/* Table */}
      {objectives.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thSt, width: 36, padding: '0.55rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    title={t.selectAll}
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th style={{ ...thSt, minWidth: 100 }}>{t.colRef}</th>
                <th style={{ ...thSt, minWidth: 200 }}>{t.colObjective}</th>
                <th style={{ ...thSt, minWidth: 110 }}>{t.colTimescale}</th>
                <th style={{ ...thSt, minWidth: 120 }}>{t.colOwner}</th>
                <th style={{ ...thSt, minWidth: 130 }}>{t.colStatus}</th>
                <th style={{ ...thSt, minWidth: 80 }}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {filteredObjectives.map(obj => {
                const objDate = obj.deadline || obj.timescale;
                const objBadge = (obj.status === 'Achieved' || obj.status === 'Not Achieved')
                  ? null
                  : dateStatus(objDate);
                return (
                  <tr
                    key={obj.id}
                    onMouseEnter={e => { e.currentTarget.style.background = BG_HOVER_LT; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                  >
                    <td style={{ ...tdSt, textAlign: 'center', padding: '0.65rem 0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(obj.id)}
                        onChange={(e) => toggleOne(obj.id, e.target.checked)}
                      />
                    </td>
                    <td style={{
                      ...tdSt, ...FONT_LABEL,
                      color: TEXT_MUTED, whiteSpace: 'nowrap',
                    }}>{obj.ref}</td>
                    <td style={{ ...tdSt, fontWeight: 600, color: TEXT_HEADING, maxWidth: 260 }}>
                      {obj.objective}
                    </td>
                    <td style={{
                      ...tdSt, ...FONT_LABEL,
                      color: TEXT_MUTED,
                      whiteSpace: 'nowrap',
                    }}>
                      {objDate || '—'}
                      {objBadge && (
                        <span style={{
                          ...FONT_TINY,
                          fontWeight: 600,
                          background: objBadge === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                          color:      objBadge === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                          borderRadius: 4,
                          padding: '0.15rem 0.45rem',
                          marginLeft: '0.35rem',
                          whiteSpace: 'nowrap',
                        }}>
                          {objBadge === 'overdue' ? t.badgeOverdue : t.badgeSoon}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdSt, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {resolvePerson(obj.owner, people) || '—'}
                    </td>
                    <td style={tdSt}>
                      <ObjStatusBadge status={obj.status} t={t} />
                    </td>
                    <td style={tdSt}>
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button
                          onClick={() => openView(obj)}
                          style={{
                            ...BTN_SECONDARY, ...FONT_SMALL, fontSize: '0.75rem',
                            borderRadius: 5, padding: '0.2rem 0.6rem',
                            cursor: 'pointer',
                          }}
                        >{t.viewBtn}</button>
                        <button
                          onClick={() => openEdit(obj)}
                          style={{
                            ...BTN_SECONDARY, ...FONT_SMALL, fontSize: '0.75rem',
                            borderRadius: 5, padding: '0.2rem 0.6rem',
                            cursor: 'pointer',
                          }}
                        >{t.editBtn}</button>
                        <button
                          onClick={() => handleDeleteOne(obj)}
                          style={{
                            ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, fontSize: '0.75rem',
                            borderRadius: 5, padding: '0.2rem 0.6rem',
                            cursor: 'pointer',
                          }}
                        >{t.deleteBtn}</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Divider />
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
